import { Checkbox, Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { ILeiData, ILeiOtherEntityNames, IZalozeniLeiInput } from 'types/leiTypes';

import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import { IcisStaty } from 'services/cisStaty';
import checkLeiApi from 'services/lei/checkLEI';

import { ETypMatky, ETypZadostiLei } from 'enums/lei/leiDataEnums';
import { ETypAdresy } from 'enums/lei/typAdresy';

import { leiJazykySelect } from 'data/codebooks/lei/jazykySelect';
import { leiCountryRegionSelect } from 'data/codebooks/lei/leiCountryRegion';
import {
    leiCountrySelect,
    leiCountrySelectFondy,
    leiCountrySelectInternational
} from 'data/codebooks/lei/leiCountrySelect';
import { leiElfKodyPermanent, leiElfKodySelect } from 'data/codebooks/lei/leiElfKodySelect';
import { leiRejstrikyPermanent, leiRejstrikySelect } from 'data/codebooks/lei/leiRejstrikySelect';
import { leiTypNazvuSelect } from 'data/codebooks/lei/leiTypNazvuSelect';

import { _getKeyValue_ } from 'utils/getObjectKeyValue';
import leiValidation from 'utils/validations/leiValidation';
import pscValidation from 'utils/validations/pscValidation';

interface IDetailInfo {
    nextStep: () => void;
    prevStep: () => void;
    setFormData: React.Dispatch<React.SetStateAction<ILeiData | undefined>>;
    formDataSpolecnost?: ILeiData['spolecnost'];
    pruvodceData?: IZalozeniLeiInput;
    entityCheck?: string;
    typZadosti?: ETypZadostiLei;
    primaMatkaLei?: string;
    cisStaty: IcisStaty[] | [];
}

const DetailInfo: React.FC<IDetailInfo> = ({
    prevStep,
    nextStep,
    setFormData,
    formDataSpolecnost,
    pruvodceData,
    entityCheck,
    typZadosti,
    primaMatkaLei,
    cisStaty
}) => {
    const [form] = Form.useForm();
    const [showDefaultValues, setShowDefaultValues] = useState<{
        region: boolean;
        hqlangregion: boolean;
        altlangregion: boolean;
    }>({
        region: true,
        hqlangregion: true,
        altlangregion: true
    });
    const [leiCheckLoading, setLeiCheckLoading] = useState<boolean>(false);
    const [leiCheckError, setLeiCheckError] = useState<string>('');
    const [disabledAddresses, setDisabledAddresses] = useState<{
        hqlangadditionaladdressline?: string;
        hqlangfirstaddressline?: string;
        hqlangcity?: string;
        hqlangpostalcode?: string;
        hqlangcountry?: string;
        hqlangregion?: string;
    }>();
    const [otherEntityNames, setOtherEntityNames] = useState<ILeiOtherEntityNames[] | undefined>([
        ...(formDataSpolecnost?.otherentitynames || []),
        {}
    ]);
    const setForceRerender = useState<boolean>(false)[1];
    const rerender = () => {
        setForceRerender(val => !val);
    };

    useEffect(() => {
        setTimeout(() => rerender(), 0); // rerender po nastaveni default values (inak sa niektore nezobrazia)
        primaMatkaLei &&
            typZadosti === ETypZadostiLei.BRANCH &&
            setDisabledAddresses({
                hqlangadditionaladdressline: 'true',
                hqlangfirstaddressline: 'true',
                hqlangcity: 'true',
                hqlangpostalcode: 'true',
                hqlangcountry: 'true',
                hqlangregion: 'true'
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatPSC = (psc: string, country: string) => {
        const splitVal = psc.replace(/\s/g, '').split('');
        splitVal.length > 3 && splitVal[3] !== ' ' && splitVal.splice(3, 0, ' ');
        const formatedPsc = splitVal.length > 3 ? splitVal.join('') : psc;

        return country === 'CZ' || country === 'SK' ? formatedPsc : psc;
    };

    const altLangValues = formDataSpolecnost?.adresy?.length
        ? formDataSpolecnost.adresy.filter(e => e.typadresy === ETypAdresy.LEGALADDRESSALTERNATIVE)
        : [];
    const hqLangValues = formDataSpolecnost?.adresy?.length
        ? formDataSpolecnost.adresy.filter(e => e.typadresy === ETypAdresy.HEADQUARTERSADDRES)
        : [];
    const althqLangValues = formDataSpolecnost?.adresy?.length
        ? formDataSpolecnost.adresy.filter(e => e.typadresy === ETypAdresy.HQTRANSALTERNATIVE)
        : [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        setFormData(pv => {
            const internationalMatka =
                pv?.matky && pv?.matky.filter(e => e.typmatky === ETypMatky.IS_INTERNATIONAL_BRANCH_OF)[0];
            return {
                ...pv,

                ...(fieldsValue.matkaslei && {
                    matky: [
                        {
                            ...(internationalMatka || []),
                            typmatky: ETypMatky.IS_INTERNATIONAL_BRANCH_OF,
                            matkaslei: fieldsValue.matkaslei
                        }
                    ]
                }),
                spolecnost: {
                    ...pv?.spolecnost,
                    jurisdikce: fieldsValue.jurisdikce,
                    adresy: [
                        {
                            typadresy: ETypAdresy.LEGALADDRESS,
                            city: fieldsValue.city,
                            country: fieldsValue.countri, // COUNTRI SA TO VOLA KVOLI CHROMOVSKEMU AUTOCOMPLETE CO NEJDE VYPNUT
                            postalcode: formatPSC(fieldsValue.postalcode, fieldsValue.countri),
                            firstaddressline: fieldsValue.firstaddressline,
                            ...(fieldsValue.region && {
                                region: fieldsValue.region
                            })
                        },
                        ...(fieldsValue.shodnaadresa
                            ? [
                                  {
                                      typadresy: ETypAdresy.HEADQUARTERSADDRES,
                                      city: fieldsValue.city,
                                      country: fieldsValue.countri,
                                      postalcode: formatPSC(fieldsValue.postalcode, fieldsValue.countri),
                                      firstaddressline: fieldsValue.firstaddressline,
                                      ...(fieldsValue.region && {
                                          region: fieldsValue.region
                                      })
                                  }
                              ]
                            : [
                                  // additionaladdress a firstlineaddress sa prehadzuju pri manualnom vyplneni (netusim preco ale musi to tak ostat)
                                  {
                                      typadresy: ETypAdresy.HEADQUARTERSADDRES,
                                      additionaladdressline: fieldsValue.hqlangfirstaddressline,
                                      city: fieldsValue.hqlangcity,
                                      country: fieldsValue.hqlangcountry,
                                      firstaddressline:
                                          typZadosti === ETypZadostiLei.FUND
                                              ? 'c/o ' + fieldsValue.hqlangadditionaladdressline
                                              : fieldsValue.hqlangadditionaladdressline,
                                      postalcode: formatPSC(fieldsValue.hqlangpostalcode, fieldsValue.hqlangcountry),

                                      ...(fieldsValue.hqlangregion && {
                                          region: fieldsValue.hqlangregion
                                      })
                                  }
                              ]),
                        ...(fieldsValue.altlanguage
                            ? [
                                  {
                                      typadresy: ETypAdresy.LEGALADDRESSALTERNATIVE,
                                      city: fieldsValue.altlangcity,
                                      country: fieldsValue.countri,
                                      firstaddressline: fieldsValue.altlangfirstaddressline,
                                      postalcode: formatPSC(fieldsValue.altlangpostalcode, fieldsValue.countri),
                                      ...(fieldsValue.altlangregion && {
                                          region: fieldsValue.altlangregion
                                      }),
                                      lang: fieldsValue.altlangcode
                                  }
                              ]
                            : []),
                        ...(!fieldsValue.shodnaadresa && fieldsValue.altlanguage
                            ? [
                                  {
                                      typadresy: ETypAdresy.HQTRANSALTERNATIVE,
                                      additionaladdressline: fieldsValue.althqlangfirstaddressline,
                                      city: fieldsValue.althqlangcity,
                                      country: fieldsValue.hqlangcountry,
                                      firstaddressline: fieldsValue.althqlangadditionaladdressline,
                                      postalcode: formatPSC(fieldsValue.althqlangpostalcode, fieldsValue.hqlangcountry),
                                      ...(fieldsValue.hqlangregion && {
                                          region: fieldsValue.hqlangregion
                                      }),
                                      lang: fieldsValue.althqlangcode
                                  }
                              ]
                            : [])
                    ],
                    entitycreationddate: fieldsValue.entitycreationddate.format('YYYY-MM-DD'),
                    entitylegalformcode: fieldsValue.entitylegalformcode,
                    ...(fieldsValue.otherlegalform && {
                        otherlegalform: fieldsValue.otherlegalform
                    }),
                    // entitystatus: fieldsValue.entitystatus,
                    legalname: fieldsValue.legalname,
                    shodnaadresa: fieldsValue.shodnaadresa,
                    registrationauthorityentityid: fieldsValue.registrationauthorityentityid,
                    registrationauthorityid: fieldsValue.registrationauthorityid,
                    /* ...(fieldsValue.transliteratedotherentitynames && {
                        transliteratedotherentitynames: fieldsValue.transliteratedotherentitynames
                    }),
                    ...(fieldsValue.transliteratedotherentitynames && {
                        transliteratedentitynametype: 'PREFERRED_ASCII_TRANSLITERATED_LEGAL_NAME'
                    }), */
                    ...(otherEntityNames?.length && {
                        otherentitynames: otherEntityNames?.filter(e => e.otherentityname)
                    })
                }
            };
        });
        nextStep();
    };

    return (
        <FormWrapper outForm={form} onFinish={onFinish}>
            <SelectComponent
                isFormItem={true}
                disabled={true}
                name="jurisdikce"
                defaultValue={formDataSpolecnost?.jurisdikce || pruvodceData?.jurisdikce}
                labelParagraph="LEI_DETAIL_JURISDICKE"
                data={
                    typZadosti === ETypZadostiLei.FUND
                        ? leiCountrySelectFondy
                        : typZadosti === ETypZadostiLei.INTERNATIONAL_ORGANIZATION
                          ? leiCountrySelectInternational
                          : leiCountrySelect
                }
            />
            <SelectComponent
                disabled={true}
                isFormItem={true}
                name="registrationauthorityid"
                showOptionValue={true}
                optionValueFilter={['RA888888']}
                defaultValue={formDataSpolecnost?.registrationauthorityid || pruvodceData?.registrationauthorityid}
                labelParagraph="REJSTRIK_KOD"
                data={[
                    ...(form.getFieldValue('jurisdikce')
                        ? _getKeyValue_(form.getFieldValue('jurisdikce'))(leiRejstrikySelect)
                        : []),
                    ...leiRejstrikyPermanent
                ]}
                onChange={val => {
                    val === 'RA000161' &&
                        form.setFieldsValue({
                            registrationauthorityentityid: 'ΗΕ'
                        });
                    rerender();
                }}
            />
            {(formDataSpolecnost?.registrationauthorityentityid || pruvodceData?.registrationauthorityentityid) && (
                <InputComponent
                    disabled={true}
                    label="LEI_ICO"
                    name="registrationauthorityentityid"
                    initialValue={
                        formDataSpolecnost?.registrationauthorityentityid || pruvodceData?.registrationauthorityentityid
                    }
                    rules={[]}
                    // rules={icoValidation()}
                    maxLength={30}
                />
            )}

            {/* typZadosti === ETypZadostiLei.RESIDENT_GOVERNMENT_ENTITY && (
                <SelectComponent
                    isFormItem={true}
                    name="subcategory"
                    defaultValue={typZadpstiSub}
                    labelParagraph="LEI_SUBCATEGORY"
                    data={leiSubCategorySelect}
                />
            )*/}
            <InputComponent
                label={'LEI_DETAIL_PRAV_SPOL'}
                name="legalname"
                disabled={entityCheck === 'OK'}
                initialValue={formDataSpolecnost?.legalname}
                maxLength={100}
            />
            {otherEntityNames?.map((e, i) => (
                <div key={i}>
                    <InputComponent
                        label="LEI_DETAIL_JINY"
                        name={`otherentityname${i}`}
                        initialValue={e.otherentityname}
                        onChange={ev =>
                            setOtherEntityNames(pv => {
                                let prevState = pv ? [...pv] : [];
                                ev.target.value === ''
                                    ? ((prevState = prevState.filter(u => u.otherentityname)),
                                      form.setFieldsValue({
                                          [`entitynametype${i}`]: undefined
                                      }))
                                    : null;
                                prevState[i] = {
                                    ...(ev.target.value ? prevState[i] : []),
                                    otherentityname: ev.target.value
                                };
                                return prevState;
                            })
                        }
                        maxLength={100}
                        notRequired={true}
                    />
                    {e.otherentityname && (
                        <SelectComponent
                            name={`entitynametype${i}`}
                            labelParagraph="LEI_DETAIL_NAZEV_TYP"
                            isFormItem={true}
                            data={leiTypNazvuSelect}
                            onChange={val =>
                                setOtherEntityNames(pv => {
                                    const prevState = pv ? [...pv] : [];
                                    prevState[i] = {
                                        ...prevState[i],
                                        entitynametype: val
                                    };
                                    prevState.length === prevState.filter(u => u.otherentityname).length &&
                                        form.getFieldValue(`lang${i}`) &&
                                        prevState.push({});
                                    return prevState;
                                })
                            }
                            defaultValue={e.entitynametype}
                        />
                    )}
                    {e.otherentityname && (
                        <SelectComponent
                            name={`lang${i}`}
                            labelParagraph="LEI_DETAIL_NAZEV_JAZYK"
                            isFormItem={true}
                            data={leiJazykySelect}
                            onChange={val =>
                                setOtherEntityNames(pv => {
                                    const prevState = pv ? [...pv] : [];
                                    prevState[i] = {
                                        ...prevState[i],
                                        lang: val
                                    };
                                    prevState.length === prevState.filter(u => u.otherentityname).length &&
                                        form.getFieldValue(`entitynametype${i}`) &&
                                        prevState.push({});
                                    return prevState;
                                })
                            }
                            defaultValue={e.lang}
                        />
                    )}
                </div>
            ))}
            {/*  <InputComponent
                label="LEI_DETAIL_JINY_TRANS"
                name="transliteratedotherentitynames"
                initialValue={formDataSpolecnost?.transliteratedotherentitynames}
                onChange={() => rerender()}
                maxLength={100}
                notRequired={true}
            />
         */}
            <SelectComponent
                name="entitylegalformcode"
                labelParagraph="LEI_DETAIL_PRAVFORMA"
                isFormItem={true}
                disabled={entityCheck === 'OK'}
                showOptionValue={true}
                optionValueFilter={['8888']}
                data={[
                    ...(Object.keys(leiElfKodySelect).includes(form.getFieldValue('countri'))
                        ? _getKeyValue_(form.getFieldValue('countri'))(leiElfKodySelect)
                        : []),
                    ...leiElfKodyPermanent
                ]}
                onChange={() => rerender()}
                defaultValue={formDataSpolecnost?.entitylegalformcode}
            />
            {form.getFieldValue('entitylegalformcode') === '8888' && (
                <InputComponent
                    label="LEI_DETAIL_PRAV_NENALEZENO"
                    name="otherlegalform"
                    initialValue={formDataSpolecnost?.otherlegalform}
                    maxLength={100}
                />
            )}

            <DatePickerComponent
                label={typZadosti === ETypZadostiLei.FUND ? 'LEI_DETAIL_DATUM_SPOL_FUND' : 'LEI_DETAIL_DATUM_SPOL'}
                name="entitycreationddate"
                infoTooltipText="LEI_DETAIL_DATUM_SPOL_TOOLTIP"
                initialValue={
                    formDataSpolecnost?.entitycreationddate ? dayjs(formDataSpolecnost.entitycreationddate) : undefined
                }
                disabled={!!formDataSpolecnost?.entitycreationddate && entityCheck === 'OK'}
                disabledDate={current => current > dayjs()}
            />
            <Title level={5} dividerTitle={true} text={'LEI_DETAIL_ADRESA_TITLE'} />
            <InputComponent
                label="ULICE_CP"
                name="firstaddressline"
                disabled={entityCheck === 'OK'}
                initialValue={
                    formDataSpolecnost?.adresy?.length ? formDataSpolecnost.adresy[0].firstaddressline : undefined
                }
                maxLength={100}
            />
            <InputComponent
                label="MESTO"
                name="city"
                initialValue={formDataSpolecnost?.adresy?.length ? formDataSpolecnost.adresy[0].city : undefined}
                disabled={entityCheck === 'OK'}
                maxLength={100}
            />
            <InputComponent
                label="PSC"
                name="postalcode"
                disabled={entityCheck === 'OK'}
                initialValue={formDataSpolecnost?.adresy?.length ? formDataSpolecnost.adresy[0].postalcode : undefined}
                rules={pscValidation('countri')}
                maxLength={10}
                usePscFormater={form.getFieldValue('countri') === 'SK' || form.getFieldValue('countri') === 'CZ'}
            />
            <SelectComponent
                disabled={entityCheck === 'OK'}
                name="countri"
                labelParagraph="STAT_SIDLA"
                isFormItem={true}
                data={cisStaty}
                onChange={() => {
                    setShowDefaultValues(pv => {
                        return {
                            ...pv,
                            region: false
                        };
                    });
                    form.setFieldsValue({
                        region: undefined
                        /*     ...(form.getFieldValue('altlanguage') && {
                            countri: val
                        }) */
                    });
                    form.validateFields(['postalcode']);
                }}
                defaultValue={
                    formDataSpolecnost?.adresy?.length
                        ? formDataSpolecnost.adresy[0].country
                        : typZadosti === ETypZadostiLei.INTERNATIONAL_ORGANIZATION
                          ? undefined
                          : pruvodceData?.jurisdikce || formDataSpolecnost?.jurisdikce
                }
            />
            {Object.keys(leiCountryRegionSelect).includes(form.getFieldValue('countri')) && (
                <SelectComponent
                    name="region"
                    labelParagraph="STAT_SIDLA_REGION"
                    isFormItem={true}
                    data={_getKeyValue_(form.getFieldValue('countri'))(leiCountryRegionSelect)}
                    onChange={() => rerender()}
                    defaultValue={
                        showDefaultValues.region && formDataSpolecnost?.adresy?.length
                            ? formDataSpolecnost.adresy[0].region
                            : undefined
                    }
                />
            )}
            <Row>
                {typZadosti !== ETypZadostiLei.FUND && typZadosti !== ETypZadostiLei.BRANCH && (
                    <Col>
                        <Form.Item
                            style={{
                                marginBottom: 8,
                                marginRight: 10,
                                fontWeight: 600
                            }}
                            name="shodnaadresa"
                            label={<TranslateText>LEI_DETAIL_ADRESA_SHODUJE</TranslateText>}
                            initialValue={
                                typZadosti === ETypZadostiLei.INTERNATIONAL_ORGANIZATION
                                    ? true
                                    : formDataSpolecnost?.adresy?.length && formDataSpolecnost.adresy.length > 1
                                      ? formDataSpolecnost.shodnaadresa
                                      : true
                            }
                            valuePropName="checked"
                        >
                            <Checkbox
                                disabled={typZadosti === ETypZadostiLei.INTERNATIONAL_ORGANIZATION}
                                style={{
                                    position: 'relative',
                                    top: -10
                                }}
                                onChange={() => rerender()}
                            />
                        </Form.Item>
                    </Col>
                )}
                <Col>
                    <Form.Item
                        style={{
                            marginBottom: 8,
                            marginLeft: 10,
                            fontWeight: 600
                        }}
                        name="altlanguage"
                        label={<TranslateText>LEI_DETAIL_ALT_JAZYK</TranslateText>}
                        initialValue={altLangValues && altLangValues.length > 0}
                        valuePropName="checked"
                    >
                        <Checkbox
                            style={{
                                position: 'relative',
                                top: -10
                            }}
                            onChange={() => rerender()}
                        />
                    </Form.Item>
                </Col>
            </Row>
            {(form.getFieldValue('shodnaadresa') !== true ||
                typZadosti === ETypZadostiLei.FUND ||
                typZadosti === ETypZadostiLei.BRANCH) && (
                <>
                    <Title
                        level={5}
                        dividerTitle={true}
                        text={typZadosti === ETypZadostiLei.FUND ? 'LEI_DETAIL_ADRESA_HQ_FUND' : 'LEI_DETAIL_ADRESA_HQ'}
                    />
                    {typZadosti === ETypZadostiLei.BRANCH && (
                        <LoadingWrapper loading={leiCheckLoading} loadingText="NACITANI_DAT">
                            <InputComponent
                                label="LEI_ZRIZOVATEL"
                                name="matkaslei"
                                initialValue={primaMatkaLei}
                                rules={[
                                    ...leiValidation(async (value: string) => {
                                        setLeiCheckLoading(true);
                                        const resp = value ? await checkLeiApi(value).catch(() => null) : null;
                                        setLeiCheckLoading(false);
                                        resp &&
                                            form.setFieldsValue({
                                                hqlangadditionaladdressline: resp.spolecnost,
                                                hqlangcountry: resp.stat,
                                                hqlangfirstaddressline: resp.adresa,
                                                hqlangcity: resp.mesto,
                                                hqlangpostalcode: resp.psc,
                                                hqlangregion:
                                                    resp.region ||
                                                    (form.getFieldValue('hqlangcity') === resp.mesto
                                                        ? form.getFieldValue('hqlangregion')
                                                        : undefined)
                                            });
                                        resp &&
                                            setDisabledAddresses({
                                                hqlangadditionaladdressline: resp.spolecnost,
                                                hqlangfirstaddressline: resp.adresa,
                                                hqlangcity: resp.mesto,
                                                hqlangpostalcode: resp.psc,
                                                hqlangcountry: resp.stat,
                                                hqlangregion: resp.region
                                            });
                                        resp &&
                                            setShowDefaultValues(pv => {
                                                return {
                                                    ...pv,
                                                    hqlangregion: false
                                                };
                                            });
                                        rerender();
                                        if (
                                            resp?.stavlei === 'RETIRED' ||
                                            resp?.stavlei === 'ANNULLED' ||
                                            resp?.stavlei === 'DUPLICATE'
                                        ) {
                                            return setLeiCheckError('CHYBNE_LEI');
                                        }
                                        if (!resp && value) {
                                            return setLeiCheckError('LEI_CHECK_ERROR');
                                        }
                                        return setLeiCheckError('');
                                    }),
                                    () => ({
                                        validator(_, value: string) {
                                            if (!value || !leiCheckError) return Promise.resolve();
                                            return Promise.reject();
                                        }
                                    })
                                ]}
                                maxLength={20}
                                onChange={() => setLeiCheckError('')}
                                validateTrigger={['onBlur', 'onSubmit']}
                                validateStatus={leiCheckError ? 'error' : undefined}
                                extramessage={leiCheckError}
                            />
                        </LoadingWrapper>
                    )}
                    <InputComponent
                        label="LEI_DETAIL_PRAV_SPOL"
                        name="hqlangadditionaladdressline"
                        addonBefore={typZadosti === ETypZadostiLei.FUND ? 'c/o' : undefined}
                        disabled={!!disabledAddresses?.hqlangadditionaladdressline}
                        initialValue={(typZadosti === ETypZadostiLei.FUND
                            ? hqLangValues?.length && hqLangValues[0].additionaladdressline
                                ? hqLangValues[0].firstaddressline
                                : undefined
                            : hqLangValues?.length && hqLangValues[0].additionaladdressline
                              ? hqLangValues[0].firstaddressline
                              : formDataSpolecnost?.legalname
                        )?.replace('c/o ', '')}
                        maxLength={100}
                    />
                    <InputComponent
                        label="ULICE_CP"
                        name="hqlangfirstaddressline"
                        disabled={!!disabledAddresses?.hqlangfirstaddressline}
                        initialValue={
                            hqLangValues?.length
                                ? hqLangValues[0].additionaladdressline
                                    ? hqLangValues[0].additionaladdressline
                                    : hqLangValues[0].firstaddressline
                                : ''
                        }
                        maxLength={100}
                    />
                    <InputComponent
                        label="MESTO"
                        name="hqlangcity"
                        disabled={!!disabledAddresses?.hqlangcity}
                        initialValue={hqLangValues?.length ? hqLangValues[0].city : ''}
                        maxLength={100}
                    />
                    <InputComponent
                        label="PSC"
                        name="hqlangpostalcode"
                        disabled={!!disabledAddresses?.hqlangpostalcode}
                        initialValue={hqLangValues?.length ? hqLangValues[0].postalcode : ''}
                        maxLength={10}
                        rules={pscValidation('hqlangcountry')}
                        usePscFormater={
                            form.getFieldValue('hqlangcountry') === 'SK' || form.getFieldValue('hqlangcountry') === 'CZ'
                        }
                    />
                    <SelectComponent
                        name="hqlangcountry"
                        disabled={!!disabledAddresses?.hqlangcountry}
                        labelParagraph="STAT"
                        isFormItem={true}
                        data={cisStaty}
                        onChange={() => {
                            setShowDefaultValues(pv => {
                                return {
                                    ...pv,
                                    hqlangregion: false
                                };
                            });
                            form.setFieldsValue({
                                hqlangregion: undefined
                            });
                            form.validateFields(['hqlangpostalcode']);
                        }}
                        defaultValue={
                            typZadosti === ETypZadostiLei.FUND
                                ? hqLangValues?.length
                                    ? hqLangValues[0].country
                                    : undefined
                                : hqLangValues?.length
                                  ? hqLangValues[0].country
                                  : pruvodceData?.jurisdikce || formDataSpolecnost?.jurisdikce
                        }
                    />
                    {Object.keys(leiCountryRegionSelect).includes(form.getFieldValue('hqlangcountry')) && (
                        <SelectComponent
                            // disabled={true}
                            name="hqlangregion"
                            labelParagraph="REGION"
                            isFormItem={true}
                            disabled={!!disabledAddresses?.hqlangregion}
                            data={_getKeyValue_(
                                form.getFieldValue('hqlangcountry') ||
                                    (hqLangValues?.length &&
                                        (hqLangValues[0].country
                                            ? hqLangValues[0].country
                                            : pruvodceData?.jurisdikce)) ||
                                    formDataSpolecnost?.jurisdikce
                            )(leiCountryRegionSelect)}
                            onChange={() => rerender()}
                            defaultValue={
                                showDefaultValues.hqlangregion
                                    ? hqLangValues?.length
                                        ? hqLangValues[0].region
                                        : formDataSpolecnost?.adresy
                                          ? formDataSpolecnost.adresy[0].region
                                          : undefined
                                    : undefined
                            }
                        />
                    )}
                </>
            )}
            {form.getFieldValue('altlanguage') === true && (
                <>
                    <Title level={5} dividerTitle={true} text={'LEI_DETAIL_ADRESA_TITLE_VARIANTA'} />
                    <SelectComponent
                        name="altlangcode"
                        labelParagraph="LEI_DETAIL_KOD_JAZYKA"
                        isFormItem={true}
                        data={leiJazykySelect}
                        onChange={() => rerender()}
                        defaultValue={altLangValues?.length ? altLangValues[0].lang : undefined}
                    />
                    <InputComponent
                        label="ULICE_CP"
                        name="altlangfirstaddressline"
                        initialValue={altLangValues?.length ? altLangValues[0].firstaddressline : ''}
                        maxLength={100}
                    />
                    <InputComponent
                        label="MESTO"
                        name="altlangcity"
                        initialValue={altLangValues?.length ? altLangValues[0].city : ''}
                        maxLength={100}
                    />
                    <InputComponent
                        label="PSC"
                        name="altlangpostalcode"
                        initialValue={altLangValues?.length ? altLangValues[0].postalcode : ''}
                        maxLength={10}
                        rules={pscValidation('countri')}
                        usePscFormater={
                            form.getFieldValue('countri') === 'SK' || form.getFieldValue('countri') === 'CZ'
                        }
                    />
                    <SelectComponent
                        disabled={true}
                        name="countri"
                        labelParagraph="STAT"
                        isFormItem={true}
                        data={cisStaty}
                        onChange={() => {
                            setShowDefaultValues(pv => {
                                return {
                                    ...pv,
                                    altlangregion: false
                                };
                            });
                            form.setFieldsValue({
                                altlangregion: undefined
                            });
                            form.validateFields(['altlangpostalcode']);
                        }}
                        defaultValue={
                            altLangValues?.length
                                ? altLangValues[0].country
                                : typZadosti === ETypZadostiLei.INTERNATIONAL_ORGANIZATION
                                  ? undefined
                                  : pruvodceData?.jurisdikce || formDataSpolecnost?.jurisdikce
                        }
                    />
                    {Object.keys(leiCountryRegionSelect).includes(form.getFieldValue('countri')) && (
                        <SelectComponent
                            disabled={true}
                            name="region"
                            labelParagraph="REGION"
                            isFormItem={true}
                            data={_getKeyValue_(
                                form.getFieldValue('countri') ||
                                    (altLangValues?.length &&
                                        (altLangValues[0].country
                                            ? altLangValues[0].country
                                            : pruvodceData?.jurisdikce)) ||
                                    formDataSpolecnost?.jurisdikce
                            )(leiCountryRegionSelect)}
                            onChange={() => rerender()}
                            defaultValue={
                                showDefaultValues.altlangregion
                                    ? altLangValues?.length
                                        ? altLangValues[0].region
                                        : formDataSpolecnost?.adresy
                                          ? formDataSpolecnost.adresy[0].region
                                          : undefined
                                    : undefined
                            }
                        />
                    )}
                </>
            )}
            {form.getFieldValue('shodnaadresa') !== true && form.getFieldValue('altlanguage') === true && (
                <>
                    <Title
                        level={5}
                        dividerTitle={true}
                        text={
                            typZadosti === ETypZadostiLei.FUND
                                ? 'LEI_DETAIL_ADRESA_HQ_FUND_VARIANTA'
                                : 'LEI_DETAIL_ADRESA_HQ_VARIANTA'
                        }
                    />
                    <SelectComponent
                        name="althqlangcode"
                        labelParagraph="LEI_DETAIL_KOD_JAZYKA"
                        isFormItem={true}
                        data={leiJazykySelect}
                        onChange={() => rerender()}
                        defaultValue={althqLangValues?.length ? althqLangValues[0].lang : undefined}
                    />
                    <InputComponent
                        label="LEI_DETAIL_PRAV_SPOL"
                        name="althqlangadditionaladdressline"
                        //  disabled={entityCheck === 'OK'}
                        initialValue={
                            typZadosti === ETypZadostiLei.FUND
                                ? althqLangValues?.length && althqLangValues[0].additionaladdressline
                                    ? althqLangValues[0].firstaddressline
                                    : undefined
                                : althqLangValues?.length && althqLangValues[0].additionaladdressline
                                  ? althqLangValues[0].firstaddressline
                                  : formDataSpolecnost?.legalname
                        }
                        maxLength={100}
                    />
                    <InputComponent
                        label="ULICE_CP"
                        name="althqlangfirstaddressline"
                        initialValue={
                            althqLangValues?.length
                                ? althqLangValues[0].additionaladdressline
                                    ? althqLangValues[0].additionaladdressline
                                    : althqLangValues[0].firstaddressline
                                : ''
                        }
                        maxLength={100}
                    />
                    <InputComponent
                        label="MESTO"
                        name="althqlangcity"
                        initialValue={althqLangValues?.length ? althqLangValues[0].city : ''}
                        maxLength={100}
                    />
                    <InputComponent
                        label="PSC"
                        name="althqlangpostalcode"
                        initialValue={althqLangValues?.length ? althqLangValues[0].postalcode : ''}
                        maxLength={10}
                        rules={pscValidation('hqlangcountry')}
                        usePscFormater={
                            form.getFieldValue('hqlangcountry') === 'SK' || form.getFieldValue('hqlangcountry') === 'CZ'
                        }
                    />
                    <SelectComponent
                        disabled={true}
                        name="hqlangcountry"
                        labelParagraph="STAT"
                        isFormItem={true}
                        data={cisStaty}
                        onChange={() => {
                            setShowDefaultValues(pv => {
                                return {
                                    ...pv,
                                    hqlangregion: false
                                };
                            });
                            form.setFieldsValue({
                                hqlangregion: undefined
                            });
                            form.validateFields(['althqlangpostalcode']);
                        }}
                        defaultValue={
                            althqLangValues?.length
                                ? althqLangValues[0].country
                                : typZadosti === ETypZadostiLei.INTERNATIONAL_ORGANIZATION
                                  ? undefined
                                  : pruvodceData?.jurisdikce || formDataSpolecnost?.jurisdikce
                        }
                    />
                    {Object.keys(leiCountryRegionSelect).includes(form.getFieldValue('hqlangcountry')) && (
                        <SelectComponent
                            disabled={true}
                            name="hqlangregion"
                            labelParagraph="REGION"
                            isFormItem={true}
                            data={_getKeyValue_(
                                form.getFieldValue('hqlangcountry') ||
                                    (althqLangValues?.length &&
                                        (althqLangValues[0].country
                                            ? althqLangValues[0].country
                                            : pruvodceData?.jurisdikce)) ||
                                    formDataSpolecnost?.jurisdikce
                            )(leiCountryRegionSelect)}
                            onChange={() => rerender()}
                            defaultValue={
                                showDefaultValues.hqlangregion
                                    ? althqLangValues?.length
                                        ? althqLangValues[0].region
                                        : formDataSpolecnost?.adresy
                                          ? formDataSpolecnost.adresy[0].region
                                          : undefined
                                    : undefined
                            }
                        />
                    )}
                </>
            )}

            <Row className="button-row">
                <ButtonComponent buttontext="ZPET" onClick={() => prevStep()} id="detailZpetBtn" type="default" />
                <ButtonComponent
                    id="detailSubmitBtn"
                    type="primary"
                    onClick={() => form.submit()}
                    buttontext="POKRACOVAT"
                />
            </Row>
        </FormWrapper>
    );
};

export default DetailInfo;
